import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck } from "lucide-react";

export default function AdminSignup() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirm: "",
    invite_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin-signup", {
        name: form.name,
        email: form.email,
        password: form.password,
        invite_code: form.invite_code,
      });
      if (data.token) localStorage.setItem("pg_token", data.token);
      toast.success(`Welcome, ${data.name}`);
      // Force a page reload so AuthContext picks up the new session
      window.location.href = "/admin";
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col" data-testid="admin-signup-page">
      <div className="p-6">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-white hover:shadow-brutal-sm transition-shadow"
        >
          <ArrowLeft size={16} /> Back to Login
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md brutal-card p-8 md:p-10 bg-white">
          <div className="mb-6 flex items-center gap-3">
            <div className="p-3 border-2 border-[#0A0A0A] rounded-xl bg-[#FF4D4D]">
              <ShieldCheck size={22} strokeWidth={2.5} className="text-white" />
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em]">Admin access</span>
              <h1 className="font-heading font-black text-3xl mt-1 tracking-tighter">Create admin account</h1>
            </div>
          </div>

          <p className="text-sm font-medium text-[#4A4A4A] mb-5">
            You'll need the hostel's <b>Admin Invite Code</b>. Ask an existing admin if you don't have one.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="admin-signup-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">Full name</label>
              <input
                data-testid="admin-signup-name"
                value={form.name}
                onChange={update("name")}
                required
                className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
                placeholder="Chandra Mouli"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">Email</label>
              <input
                data-testid="admin-signup-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
                placeholder="warden@vasavihostel.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2">Password</label>
                <input
                  data-testid="admin-signup-password"
                  type="password"
                  value={form.password}
                  onChange={update("password")}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
                  placeholder="min 6 chars"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2">Confirm</label>
                <input
                  data-testid="admin-signup-confirm"
                  type="password"
                  value={form.confirm}
                  onChange={update("confirm")}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">Admin invite code</label>
              <input
                data-testid="admin-signup-invite"
                value={form.invite_code}
                onChange={update("invite_code")}
                required
                className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
                placeholder="Enter the invite code from your hostel"
              />
            </div>

            {error && (
              <div
                data-testid="admin-signup-error"
                className="bg-[#FF4D4D] text-white border-2 border-[#0A0A0A] rounded-xl px-4 py-2 text-sm font-bold"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-signup-submit"
              className="w-full inline-flex items-center justify-center gap-2 font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#FF4D4D] text-white shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-60"
            >
              {loading ? "Creating account..." : "Create admin account"}
            </button>
          </form>

          <div className="mt-5 text-xs font-medium text-[#4A4A4A] text-center">
            Already have an admin account?{" "}
            <Link to="/login" className="font-bold underline underline-offset-4">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
